import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { Spin } from 'antd'
import { useGetLanguagesQuery } from '../api/v2/languages'
import { useUpdateUserMutation } from '../api/v2/users'
import { useMyContext } from '../context/context'
import MyLoading from '../components/MyLoading'
import useSettingsBo from './useSettingsBO'

const alias = { cn: 'zh-cn' }
const cache = {}

const load = l => cache[l] || (cache[l] = l === 'en' ? Promise.resolve() : import(`dayjs/locale/${l}.js`).catch(() => Promise.resolve()))

export const setDayjsLocale = async lang => {
  const l = alias[lang] || lang
  await load(l)
  dayjs.locale(l)
}

const navigatorLanguage = navigator.language || navigator.userLanguage
const languageCode = navigatorLanguage.substring(0, 2).toUpperCase()

const LanguageContext = createContext(null)

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useCounter must be used within <CounterProvider>')
  return ctx
}

export function LanguageProvider({ children }) {
  const settings = useSettingsBo()
  const { myContext, user: me } = useMyContext()

  const [languageId, setLanguageId] = useState(Number(localStorage.getItem('languageId')))

  const { data, isLoading } = useGetLanguagesQuery({ ...myContext, userId: undefined })
  const [update] = useUpdateUserMutation()

  const languages = useMemo(() => data?.filter(f => settings?.LANGUAGES_SHOW?.find(ff => ff === f.id) || f.key === 'FR'), [data, settings])

  const language = useMemo(() => {
    if (languages?.length > 0) {
      return languages.find(f => f.id === languageId)
    }
    return null
  }, [languageId, languages])

  const languageDefault = useMemo(() => {
    if (languages && settings) {
      return languages.find(f => f.id === settings.LANGUAGE_DEFAULT)
    }
    return null
  }, [languages, settings])

  const languageFR = useMemo(() => {
    if (languages) {
      return languages.find(f => f.key === 'FR')
    }
    return null
  }, [languages])

  useEffect(() => {
    const languageNavigator = languages?.find(f => f.key === languageCode)
    setLanguageId(me?.languageId || Number(localStorage.getItem('languageId')) || languageNavigator?.id || settings?.LANGUAGE_DEFAULT)
  }, [languages, me, settings])

  useEffect(() => {
    if (language) {
      setDayjsLocale(language.key.toLowerCase())
    }
  }, [language])

  const updateLanguageId = useCallback(
    async id => {
      localStorage.setItem('languageId', id)
      setLanguageId(id)
      if (me) {
        await update({ ...myContext, user: { languageId: id } }).unwrap()
      }
    },
    [me, myContext, setLanguageId, update]
  )

  const value = useMemo(
    () => ({
      languages,
      language,
      languageDefault,
      languageFR,
      languageId,
      updateLanguageId,
    }),
    [language, languageDefault, languageFR, languageId, languages, updateLanguageId]
  )

  return (
    <MyLoading loading={isLoading}>
      {isLoading ? <div /> : <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>}
    </MyLoading>
  )
}
